import { ButtonLink } from '@domgen/base-design-system-2.0/atoms/ButtonLink';
import { SystemCode, WhirlpoolQrSystemCode } from '@domgen/shared/models/systemCode';
import { gaEvent } from '@domgen/shared/services/ga/ga';
import posthog from 'posthog-js';
import { Computer } from '../../../assets/icons/Computer';
import { Phone } from '../../../assets/icons/Phone';
import { CALL_US_MODAL_NUMBER, CALL_US_MODAL_TEL_HREF } from '../../../const/callUs';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { store } from '../../../store/store';
import type { ModelSerialLandingFormProps } from './ModelSerialLandingOnlineForm.types';
import { ModelSerialLandingOnlineFormCallUs } from './ModelSerialLandingOnlineFormCallUs';

export const CircleCheck = () => {
  return (
    <svg
      className="shrink-0"
      width="20"
      height="24"
      viewBox="0 0 17 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.375 1C5.84375 1 3.5625 2.34375 2.3125 4.5C1.03125 6.6875 1.03125 9.34375 2.3125 11.5C3.5625 13.6875 5.84375 15 8.375 15C10.875 15 13.1562 13.6875 14.4062 11.5C15.6875 9.34375 15.6875 6.6875 14.4062 4.5C13.1562 2.34375 10.875 1 8.375 1ZM8.375 16C5.5 16 2.875 14.5 1.4375 12C0 9.53125 0 6.5 1.4375 4C2.875 1.53125 5.5 0 8.375 0C11.2188 0 13.8438 1.53125 15.2812 4C16.7188 6.5 16.7188 9.53125 15.2812 12C13.8438 14.5 11.2188 16 8.375 16ZM11.7188 6.375L7.71875 10.375C7.53125 10.5625 7.1875 10.5625 7 10.375L5 8.375C4.8125 8.1875 4.8125 7.84375 5 7.65625C5.1875 7.46875 5.53125 7.46875 5.71875 7.65625L7.375 9.3125L11 5.65625C11.1875 5.46875 11.5312 5.46875 11.7188 5.65625C11.9062 5.84375 11.9062 6.1875 11.7188 6.375Z"
        fill="#3B8524"
      />
    </svg>
  );
};

const styles = {
  heroBox:
    'bg-(--color-bg-main-container) p-space-700 flex flex-col gap-space-500 w-full max-w-7xl shadow-lg',
  heading: 'text-h1 text-center',
  subtitle: 'text-body-large text-center mb-space-200 text-text-body max-w-4xl self-center',
  panels: 'grid grid-cols-1 lg:grid-cols-2 gap-space-500 w-full',
  panel:
    'flex flex-col gap-space-500 rounded-radius-100 border border-neutral-900 bg-(--color-bg-main-container) p-space-500',
  panelTitle:
    'text-h3 text-center text-primary-600 text-action-default inline-flex items-center gap-space-400 w-full justify-center',
  panelBody: 'text-body-large text-center text-text-body',
  benefits:
    'flex flex-col gap-space-300 rounded-radius-100 bg-primary-100 px-space-400 py-space-300',
  benefit: 'flex items-start gap-space-200 text-body text-text-body',
  onlineCtaRow: 'flex flex-col gap-space-400',
  phoneButtonContainer: 'w-full pt-space-200',
};

type ModelSerialLandingCallUsProps = {
  brandFriendlyName: string;
  formProps: ModelSerialLandingFormProps;
  systemCode: SystemCode | WhirlpoolQrSystemCode | undefined;
  experimentName?: string;
  experimentVariant?: string;
};

const callUsBenefits = [
  'Understand your warranty and registration benefits',
  'Find your model or serial number',
  'Register multiple appliances',
];

export function ModelSerialLandingCallUs({
  brandFriendlyName,
  formProps,
  systemCode,
  experimentName,
  experimentVariant,
}: ModelSerialLandingCallUsProps) {
  const onCallUsClick = () => {
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'call-us-clicked',
      eventLabel: `Call now`,
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      experimentName,
      experimentVariant,
    });
    capturePostHogEvent(posthog, 'call-us-landing-clicked', store.get());
  };

  return (
    <div className={styles.heroBox}>
      <h1 className={styles.heading}>Register your appliance in a few simple steps</h1>
      <p className={styles.subtitle}>
        It only takes a few minutes to unlock additional benefits, support and peace of mind.
      </p>
      <div className={styles.panels}>
        <article className={styles.panel}>
          <h2 className={styles.panelTitle}>
            <Phone /> By Phone
          </h2>
          <p className={styles.panelBody}>
            Speak to a {brandFriendlyName} expert who can help you:
          </p>
          <div className={styles.benefits}>
            {callUsBenefits.map((benefit) => (
              <div key={benefit} className={styles.benefit}>
                <CircleCheck />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
          <div className={styles.phoneButtonContainer}>
            <ButtonLink href={CALL_US_MODAL_TEL_HREF} variant="primary" onClick={onCallUsClick}>
              Call now<span className="hidden md:inline"> - {CALL_US_MODAL_NUMBER}</span>
            </ButtonLink>
          </div>
        </article>
        <article className={styles.panel}>
          <h2 className={styles.panelTitle}>
            <Computer /> ONLINE
          </h2>
          <p className={styles.panelBody}>Enter your model and serial number to get started</p>
          <div className={styles.onlineCtaRow}>
            <ModelSerialLandingOnlineFormCallUs {...formProps} />
          </div>
        </article>
      </div>
    </div>
  );
}
